/**
 * Lightbox Module
 * Modal overlay for viewing Instagram posts with images, videos, and captions
 */

export class Lightbox {
  /**
   * Creates a lightbox instance
   * @param {Array} posts - Array of Instagram posts
   * @param {Object} config - Lightbox configuration options
   */
  constructor(posts, config = {}) {
    this.posts = posts;
    this.currentIndex = 0;
    this.modal = null;
    this.isOpen = false;

    // Configuration options
    this.config = {
      // Link visibility
      showViewOnInstagram: config.showViewOnInstagram !== false,
      showUsername: config.showUsername !== false,

      // UI elements visibility
      showCounter: config.showCounter !== false,
      showPostType: config.showPostType !== false,
      showTimestamp: config.showTimestamp !== false,
      showNavigation: config.showNavigation !== false,
      showCloseButton: config.showCloseButton !== false,

      // Video settings
      videoControls: config.videoControls !== false,
      videoAutoplay: config.videoAutoplay !== false,
      videoLoop: config.videoLoop !== false,
      videoMuted: config.videoMuted || false
    };
  }

  /**
   * Opens lightbox at specific post index
   * @param {number} index - Post index
   */
  open(index = 0) {
    this.currentIndex = index;
    this.isOpen = true;
    this.render();
    this.attachEventListeners();

    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  /**
   * Closes the lightbox
   */
  close() {
    if (this.modal) {
      this.modal.remove();
      this.modal = null;
    }
    this.isOpen = false;

    // Restore body scroll
    document.body.style.overflow = '';
  }

  /**
   * Navigates to next post (loops to first if at end)
   */
  next() {
    if (this.currentIndex < this.posts.length - 1) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0; // Loop to first post
    }
    this.updateContent();
  }

  /**
   * Navigates to previous post (loops to last if at beginning)
   */
  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = this.posts.length - 1; // Loop to last post
    }
    this.updateContent();
  }

  /**
   * Renders the lightbox modal
   */
  render() {
    const post = this.posts[this.currentIndex];

    // Create modal element
    this.modal = document.createElement('div');
    this.modal.className = 'ig-lightbox';
    this.modal.innerHTML = `
      <div class="ig-lightbox-backdrop"></div>
      <div class="ig-lightbox-container">
        ${this.config.showCloseButton ? '<button class="ig-lightbox-close" aria-label="Close">&times;</button>' : ''}

        ${this.config.showNavigation ? `
          <button class="ig-lightbox-nav ig-lightbox-prev" aria-label="Previous">
            ‹
          </button>

          <button class="ig-lightbox-nav ig-lightbox-next" aria-label="Next">
            ›
          </button>
        ` : ''}

        <div class="ig-lightbox-content">
          <div class="ig-lightbox-media-container">
            ${this.renderMedia(post)}
          </div>

          <div class="ig-lightbox-sidebar">
            ${(this.config.showUsername || this.config.showViewOnInstagram) ? `
              <div class="ig-lightbox-header">
                ${this.config.showUsername ? `
                  <a href="${post.permalink}" target="_blank" rel="noopener noreferrer" class="ig-lightbox-username">
                    @${this.escapeHtml(post.username)}
                  </a>
                ` : ''}
                ${this.config.showViewOnInstagram ? `
                  <a href="${post.permalink}" target="_blank" rel="noopener noreferrer" class="ig-lightbox-instagram-link">
                    View on Instagram →
                  </a>
                ` : ''}
              </div>
            ` : ''}

            <div class="ig-lightbox-caption">
              ${post.caption ? this.formatCaption(post.caption) : '<em>No caption</em>'}
            </div>

            ${(this.config.showTimestamp || this.config.showPostType) ? `
              <div class="ig-lightbox-meta">
                ${this.config.showTimestamp ? `
                  <time datetime="${post.timestamp}">
                    ${this.formatDate(post.timestamp)}
                  </time>
                ` : ''}
                ${this.config.showPostType ? `
                  <span class="ig-lightbox-type">
                    ${this.getPostTypeLabel(post.media_type)}
                  </span>
                ` : ''}
              </div>
            ` : ''}
          </div>
        </div>

        ${this.config.showCounter ? `
          <div class="ig-lightbox-counter">
            ${this.currentIndex + 1} / ${this.posts.length}
          </div>
        ` : ''}
      </div>
    `;

    document.body.appendChild(this.modal);

    // Add auto-advance for videos on initial render
    if (post.media_type === 'VIDEO') {
      const video = this.modal.querySelector('video');
      if (video) {
        video.addEventListener('ended', () => {
          this.next();
        }, { once: true }); // Use once to auto-remove listener
      }
    }

    // Trigger animation
    requestAnimationFrame(() => {
      this.modal.classList.add('ig-lightbox-open');
    });
  }

  /**
   * Updates lightbox content when navigating
   */
  updateContent() {
    const post = this.posts[this.currentIndex];

    // Update media
    const mediaContainer = this.modal.querySelector('.ig-lightbox-media-container');
    mediaContainer.innerHTML = this.renderMedia(post);

    // Add auto-advance for videos
    if (post.media_type === 'VIDEO') {
      const video = mediaContainer.querySelector('video');
      if (video) {
        video.addEventListener('ended', () => {
          this.next();
        }, { once: true }); // Use once to auto-remove listener
      }
    }

    // Update sidebar elements if they exist
    if (this.config.showUsername) {
      const username = this.modal.querySelector('.ig-lightbox-username');
      if (username) {
        username.textContent = `@${post.username}`;
        username.href = post.permalink;
      }
    }

    if (this.config.showViewOnInstagram) {
      const instagramLink = this.modal.querySelector('.ig-lightbox-instagram-link');
      if (instagramLink) {
        instagramLink.href = post.permalink;
      }
    }

    const caption = this.modal.querySelector('.ig-lightbox-caption');
    if (caption) {
      caption.innerHTML = post.caption ? this.formatCaption(post.caption) : '<em>No caption</em>';
    }

    if (this.config.showTimestamp) {
      const timestamp = this.modal.querySelector('time');
      if (timestamp) {
        timestamp.setAttribute('datetime', post.timestamp);
        timestamp.textContent = this.formatDate(post.timestamp);
      }
    }

    if (this.config.showPostType) {
      const typeLabel = this.modal.querySelector('.ig-lightbox-type');
      if (typeLabel) {
        typeLabel.textContent = this.getPostTypeLabel(post.media_type);
      }
    }

    // Update counter if visible
    if (this.config.showCounter) {
      const counter = this.modal.querySelector('.ig-lightbox-counter');
      if (counter) {
        counter.textContent = `${this.currentIndex + 1} / ${this.posts.length}`;
      }
    }

    // Navigation buttons are always enabled (they loop around)
  }

  /**
   * Renders media (image or video) for a post
   * @param {Object} post - Instagram post
   * @returns {string} Media HTML
   */
  renderMedia(post) {
    if (post.media_type === 'VIDEO') {
      return `
        <video
          class="ig-lightbox-video"
          ${this.config.videoControls ? 'controls' : ''}
          ${this.config.videoAutoplay ? 'autoplay' : ''}
          ${this.config.videoMuted ? 'muted' : ''}
          src="${post.media_url}"
          poster="${post.thumbnail_url || ''}"
        >
          Your browser does not support video playback.
        </video>
      `;
    } else {
      return `
        <img
          class="ig-lightbox-image"
          src="${post.media_url}"
          alt="${this.escapeHtml(post.caption || 'Instagram post')}"
        />
      `;
    }
  }

  /**
   * Formats caption with hashtags and mentions as links
   * @param {string} caption - Post caption
   * @returns {string} Formatted HTML
   */
  formatCaption(caption) {
    let formatted = this.escapeHtml(caption);

    // Convert hashtags to links
    formatted = formatted.replace(
      /#(\w+)/g,
      '<a href="https://www.instagram.com/explore/tags/$1/" target="_blank" rel="noopener noreferrer" class="ig-lightbox-hashtag">#$1</a>'
    );

    // Convert mentions to links
    formatted = formatted.replace(
      /@(\w+)/g,
      '<a href="https://www.instagram.com/$1/" target="_blank" rel="noopener noreferrer" class="ig-lightbox-mention">@$1</a>'
    );

    // Convert line breaks
    formatted = formatted.replace(/\n/g, '<br>');

    return formatted;
  }

  /**
   * Formats timestamp into readable date
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Formatted date
   */
  formatDate(timestamp) {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }

  /**
   * Gets human-readable label for post type
   * @param {string} mediaType - Media type (IMAGE, VIDEO, CAROUSEL_ALBUM)
   * @returns {string} Label
   */
  getPostTypeLabel(mediaType) {
    switch (mediaType) {
      case 'VIDEO':
        return '🎥 Video';
      case 'CAROUSEL_ALBUM':
        return '🖼️ Album';
      case 'IMAGE':
      default:
        return '📷 Photo';
    }
  }

  /**
   * Attaches event listeners to lightbox controls
   */
  attachEventListeners() {
    // Close button
    if (this.config.showCloseButton) {
      const closeBtn = this.modal.querySelector('.ig-lightbox-close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => this.close());
      }
    }

    // Backdrop click
    const backdrop = this.modal.querySelector('.ig-lightbox-backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', () => this.close());
    }

    // Navigation buttons
    if (this.config.showNavigation) {
      const prevBtn = this.modal.querySelector('.ig-lightbox-prev');
      const nextBtn = this.modal.querySelector('.ig-lightbox-next');

      if (prevBtn && nextBtn) {
        prevBtn.addEventListener('click', () => this.prev());
        nextBtn.addEventListener('click', () => this.next());
      }
    }

    // Keyboard navigation
    this.keyHandler = (e) => {
      if (!this.isOpen) return;

      switch (e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          if (this.config.showNavigation) this.prev();
          break;
        case 'ArrowRight':
          if (this.config.showNavigation) this.next();
          break;
      }
    };

    document.addEventListener('keydown', this.keyHandler);
  }

  /**
   * Escapes HTML to prevent XSS
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Cleans up event listeners
   */
  destroy() {
    if (this.keyHandler) {
      document.removeEventListener('keydown', this.keyHandler);
    }
    this.close();
  }
}

export default Lightbox;
